export { resolveThemeForRuntime, themeTokensToCssVars } from '../chunk-ZR45FGW3.js';
import { useUISlotManager } from '../chunk-L3MTZ3RH.js';
import '../chunk-PEST66PN.js';
import '../chunk-QPXIAUJD.js';
import { createContext, useMemo, useContext, useState, useEffect, useCallback } from 'react';
import { jsx } from 'react/jsx-runtime';
import { createLogger } from '@almadar/logger';
export { ANONYMOUS_USER, ARBITRARY_CLASS_STYLE_ID, ArbitraryClassCompilerProvider, CurrentPagePathContext, CurrentPagePathProvider, OrbitalThemeProvider, UserContext, UserProvider, useArbitraryClassStyles, useCurrentPagePath, useHasPermission, useHasRole, useUser, useUserForEvaluation } from '@almadar/ui/providers';
import { ThemeProvider as ThemeProvider$1, useTheme as useTheme$1 } from '@almadar/ui/context';

var UISlotContext = createContext(null);
function UISlotProvider({ children }) {
  const slotManager = useUISlotManager();
  const contextValue = useMemo(() => slotManager, [slotManager]);
  return /* @__PURE__ */ jsx(UISlotContext.Provider, { value: contextValue, children });
}
function useUISlots() {
  const context = useContext(UISlotContext);
  if (!context) {
    throw new Error(
      "useUISlots must be used within a UISlotProvider. Make sure your component tree is wrapped with <UISlotProvider>."
    );
  }
  return context;
}
function useSlotContent(slot) {
  const { getContent } = useUISlots();
  return getContent(slot);
}
function useSlotHasContent(slot) {
  const { hasContent } = useUISlots();
  return hasContent(slot);
}

// providers/builtInThemes.ts
var BUILT_IN_THEMES = [
  { name: "almadar", displayName: "Almadar", hasLightMode: true, hasDarkMode: true },
  { name: "almadar-heritage", displayName: "Almadar Heritage", hasLightMode: true, hasDarkMode: true },
  { name: "almadar-website", displayName: "Almadar Website", hasLightMode: true, hasDarkMode: true },
  { name: "aqua", displayName: "Aqua Gel", hasLightMode: true, hasDarkMode: true },
  { name: "arabesque", displayName: "Arabesque", hasLightMode: true, hasDarkMode: true },
  { name: "art-deco", displayName: "Art Deco 1925", hasLightMode: true, hasDarkMode: true },
  { name: "art-nouveau", displayName: "Art Nouveau", hasLightMode: true, hasDarkMode: true },
  { name: "atelier", displayName: "Atelier", hasLightMode: true, hasDarkMode: true },
  { name: "bauhaus", displayName: "Bauhaus", hasLightMode: true, hasDarkMode: true },
  { name: "bloomberg-dense", displayName: "Bloomberg Dense", hasLightMode: true, hasDarkMode: true },
  { name: "blueprint", displayName: "Blueprint", hasLightMode: true, hasDarkMode: true },
  { name: "clay", displayName: "Clay", hasLightMode: true, hasDarkMode: true },
  { name: "comic", displayName: "Comic Studio", hasLightMode: true, hasDarkMode: true },
  { name: "constructivist", displayName: "Constructivist", hasLightMode: true, hasDarkMode: true },
  { name: "corporate", displayName: "Corporate", hasLightMode: true, hasDarkMode: true },
  { name: "cyberpunk", displayName: "Cyberpunk HUD", hasLightMode: true, hasDarkMode: true },
  { name: "frutiger-aero", displayName: "Frutiger Aero", hasLightMode: true, hasDarkMode: true },
  { name: "game-adventure", displayName: "Game: Adventure", hasLightMode: false, hasDarkMode: true },
  { name: "game-rpg", displayName: "Game: RPG", hasLightMode: false, hasDarkMode: true },
  { name: "game-sci-fi", displayName: "Game: Sci-Fi", hasLightMode: false, hasDarkMode: true },
  { name: "game-ui-pack", displayName: "Game: Flat UI", hasLightMode: false, hasDarkMode: true },
  { name: "gazette", displayName: "Gazette", hasLightMode: true, hasDarkMode: true },
  { name: "glass", displayName: "Glass", hasLightMode: true, hasDarkMode: true },
  { name: "gothic", displayName: "Gothic Manuscript", hasLightMode: true, hasDarkMode: true },
  { name: "ink-wash", displayName: "Ink Wash", hasLightMode: true, hasDarkMode: true },
  { name: "kawaii", displayName: "Kawaii", hasLightMode: true, hasDarkMode: true },
  { name: "kflow", displayName: "KFlow", hasLightMode: true, hasDarkMode: true },
  { name: "kiosk", displayName: "Kiosk", hasLightMode: true, hasDarkMode: true },
  { name: "linear-clean", displayName: "Linear Clean", hasLightMode: true, hasDarkMode: true },
  { name: "luxury", displayName: "Haute Luxe", hasLightMode: true, hasDarkMode: true },
  { name: "mac-classic", displayName: "Classic Mac OS", hasLightMode: true, hasDarkMode: true },
  { name: "material", displayName: "Material 3", hasLightMode: true, hasDarkMode: true },
  { name: "memphis", displayName: "Memphis 80s", hasLightMode: true, hasDarkMode: true },
  { name: "mid-century", displayName: "Mid-Century Modern", hasLightMode: true, hasDarkMode: true },
  { name: "minimalist", displayName: "Minimalist", hasLightMode: true, hasDarkMode: true },
  { name: "mudcloth", displayName: "Mudcloth", hasLightMode: true, hasDarkMode: true },
  { name: "neon", displayName: "Neon", hasLightMode: true, hasDarkMode: true },
  { name: "neumorphic", displayName: "Neumorphic", hasLightMode: true, hasDarkMode: true },
  { name: "newsprint", displayName: "Newsprint", hasLightMode: true, hasDarkMode: true },
  { name: "notion-editorial", displayName: "Notion Editorial", hasLightMode: true, hasDarkMode: true },
  { name: "orb", displayName: "Orb", hasLightMode: true, hasDarkMode: true },
  { name: "pixel", displayName: "Pixel Quest", hasLightMode: true, hasDarkMode: true },
  { name: "prism", displayName: "Prism", hasLightMode: true, hasDarkMode: true },
  { name: "retro", displayName: "Retro", hasLightMode: true, hasDarkMode: true },
  { name: "risograph", displayName: "Risograph", hasLightMode: true, hasDarkMode: true },
  { name: "scandi", displayName: "Scandinavian", hasLightMode: true, hasDarkMode: true },
  { name: "sketch", displayName: "Sketchbook", hasLightMode: true, hasDarkMode: true },
  { name: "solarpunk", displayName: "Solarpunk", hasLightMode: true, hasDarkMode: true },
  { name: "steampunk", displayName: "Steampunk", hasLightMode: true, hasDarkMode: true },
  { name: "swiss", displayName: "Swiss International", hasLightMode: true, hasDarkMode: true },
  { name: "terminal", displayName: "Terminal", hasLightMode: true, hasDarkMode: true },
  { name: "trait-wars", displayName: "Trait Wars", hasLightMode: true, hasDarkMode: true },
  { name: "ukiyo-e", displayName: "Ukiyo-e", hasLightMode: true, hasDarkMode: true },
  { name: "vaporwave", displayName: "Vaporwave", hasLightMode: true, hasDarkMode: true },
  { name: "victorian", displayName: "Victorian", hasLightMode: true, hasDarkMode: true },
  { name: "wabi-sabi", displayName: "Wabi-Sabi", hasLightMode: true, hasDarkMode: true },
  { name: "win95", displayName: "Windows 95", hasLightMode: true, hasDarkMode: true },
  { name: "wireframe", displayName: "Wireframe", hasLightMode: true, hasDarkMode: true },
  { name: "zine", displayName: "Punk Zine", hasLightMode: true, hasDarkMode: true }
];
var log = createLogger("almadar:ui:theme");
var ThemeContext = createContext(void 0);
var ThemeScopeContext = createContext({});
function useThemeScope() {
  return useContext(ThemeScopeContext);
}
function registryKeyMode(key, themes) {
  return registryKeyTheme(key, themes)?.mode;
}
function registryKeyTheme(key, themes) {
  for (const t of themes) {
    if (key === `${t.name}-light` && themeHasMode(t, "light")) return { theme: t, mode: "light" };
    if (key === `${t.name}-dark` && themeHasMode(t, "dark")) return { theme: t, mode: "dark" };
  }
  return void 0;
}
function themeHasMode(theme, mode) {
  return mode === "light" ? theme.hasLightMode !== false : theme.hasDarkMode !== false;
}
function useScopedMode() {
  const scope = useThemeScope();
  const { resolvedMode } = useTheme();
  return scope.mode ?? resolvedMode;
}
var THEME_STORAGE_KEY = "theme";
var MODE_STORAGE_KEY = "theme-mode";
function getSystemMode() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function resolveMode(mode) {
  if (mode === "system") {
    return getSystemMode();
  }
  return mode;
}
var ThemeProvider = ({
  children,
  themes = [],
  defaultTheme = "wireframe",
  defaultMode = "system",
  targetRef
}) => {
  const availableThemes = useMemo(() => {
    const themeMap = /* @__PURE__ */ new Map();
    BUILT_IN_THEMES.forEach((t) => themeMap.set(t.name, t));
    themes.forEach((t) => themeMap.set(t.name, t));
    return Array.from(themeMap.values());
  }, [themes]);
  const isScoped = !!targetRef;
  const [theme, setThemeState] = useState(() => {
    if (isScoped || typeof window === "undefined") return defaultTheme;
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    const validThemes = [
      ...BUILT_IN_THEMES.map((t) => t.name),
      ...themes.map((t) => t.name)
    ];
    if (stored && validThemes.includes(stored)) {
      return stored;
    }
    return defaultTheme;
  });
  const [mode, setModeState] = useState(() => {
    if (isScoped || typeof window === "undefined") return defaultMode;
    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
    return defaultMode;
  });
  const [resolvedMode, setResolvedMode] = useState(
    () => resolveMode(mode)
  );
  const appliedTheme = useMemo(
    () => `${theme}-${resolvedMode}`,
    [theme, resolvedMode]
  );
  useEffect(() => {
    const updateResolved = () => {
      setResolvedMode(resolveMode(mode));
    };
    updateResolved();
    if (mode === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => updateResolved();
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    return void 0;
  }, [mode]);
  useEffect(() => {
    if (isScoped) {
      if (targetRef?.current) {
        targetRef.current.setAttribute("data-theme", appliedTheme);
        targetRef.current.classList.remove("light", "dark");
        targetRef.current.classList.add(resolvedMode);
      }
      return;
    }
    const root = document.documentElement;
    root.setAttribute("data-theme", appliedTheme);
    root.style.colorScheme = resolvedMode;
    root.classList.remove("light", "dark");
    root.classList.add(resolvedMode);
  }, [appliedTheme, resolvedMode, targetRef, isScoped]);
  const setTheme = useCallback(
    (newTheme) => {
      const validTheme = availableThemes.find((t) => t.name === newTheme);
      if (validTheme) {
        setThemeState(newTheme);
        if (!isScoped && typeof window !== "undefined") {
          localStorage.setItem(THEME_STORAGE_KEY, newTheme);
        }
      } else {
        log.warn("Theme not found", {
          theme: newTheme,
          available: availableThemes.map((t) => t.name)
        });
      }
    },
    [availableThemes]
  );
  const setMode = useCallback((newMode) => {
    setModeState(newMode);
    if (!isScoped && typeof window !== "undefined") {
      localStorage.setItem(MODE_STORAGE_KEY, newMode);
    }
  }, []);
  const toggleMode = useCallback(() => {
    const newMode = resolvedMode === "dark" ? "light" : "dark";
    setMode(newMode);
  }, [resolvedMode, setMode]);
  const contextValue = useMemo(
    () => ({
      theme,
      mode,
      resolvedMode,
      setTheme,
      setMode,
      toggleMode,
      availableThemes,
      appliedTheme
    }),
    [
      theme,
      mode,
      resolvedMode,
      setTheme,
      setMode,
      toggleMode,
      availableThemes,
      appliedTheme
    ]
  );
  const scope = useMemo(() => ({ theme: appliedTheme, mode: resolvedMode }), [appliedTheme, resolvedMode]);
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx(ThemeScopeContext.Provider, { value: scope, children }) });
};
function useTheme() {
  const context = useContext(ThemeContext);
  if (context === void 0) {
    return {
      theme: "wireframe",
      mode: "light",
      resolvedMode: "light",
      setTheme: () => {
      },
      setMode: () => {
      },
      toggleMode: () => {
      },
      availableThemes: BUILT_IN_THEMES,
      appliedTheme: "wireframe-light"
    };
  }
  return context;
}
var ThemeContext_default = ThemeContext;
var DesignThemeProvider = ThemeProvider$1;
function useDesignTheme() {
  const { theme, setTheme, availableThemes } = useTheme$1();
  return {
    designTheme: theme,
    setDesignTheme: setTheme,
    availableThemes: availableThemes.map((t) => t.name)
  };
}

export { BUILT_IN_THEMES, DesignThemeProvider, ThemeContext_default as ThemeContext, ThemeProvider, ThemeScopeContext, UISlotContext, UISlotProvider, registryKeyMode, registryKeyTheme, themeHasMode, useDesignTheme, useScopedMode, useSlotContent, useSlotHasContent, useTheme, useThemeScope, useUISlots };
